<template>
  <div class="country-map-container">
    <div class="map-layer" v-html="svg" @click="handleClick"></div>

    <div v-if="selectedCountry" class="stats-overlay" @click.self="closePopup">
      <div class="stats-card">
        <div class="stats-card__header-bg">
          <div class="header-content">
            <div class="country-section">
              <span class="country-name">{{ selectedCountry.name }}</span>
              <img :src="getFlagUrl(selectedCountry.id)" class="flag-icon" />
            </div>

            <div
              class="stats-card__arrow-link"
              style="cursor: pointer;"
              @click="$emit('select-country', selectedCountry)"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
        </div>

        <div class="stats-grid">
          <div v-for="(val, key) in displayStats" :key="key" class="stat-box">
            <div class="icon-wrapper">
              <img :src="val.svgPath" class="stat-icon" alt="" />
            </div>

            <span class="stat-label">
              {{ val.label }}
            </span>

            <h2 class="stat-value">
              {{ selectedCountry.stats[val.key] || '0' }}
            </h2>

            <span class="stat-meta"> in {{ selectedCountry.name }} in {{ val.year }} </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapUrl from './europe-map.svg'
import { countryData } from './countryData.js'
import CircularIcon from '../../assets/img/we-circular/Stats/Circular.svg'
import CloudIcon from '../../assets/img/we-circular/Stats/Cloud.svg'
import MonitorIcon from '../../assets/img/we-circular/Stats/Monitor.svg'
import GraphIcon from '../../assets/img/we-circular/Stats/Graph.svg'
import BagIcon from '../../assets/img/we-circular/Stats/Bag.svg'
import PlusIcon from '../../assets/img/we-circular/Stats/Plus.svg'

export default {
  name: 'CountryMap',
  data() {
    return {
      svg: '',
      selectedCountry: null,
      countryData: countryData,
      kpis: {
        'SME Digital Intensity': { key: 'digitalIntensity', color: 'blue', max: 100 },
        'Labour Productivity Index': { key: 'productivity', color: 'green', max: 150 },
        'Women Entrepreneurship': { key: 'womenEntrepreneurs', color: 'orange', max: 100 }
      },
      displayStats: [
        {
          key: 'circularity',
          label: 'CMU',
          year: '2024',
          svgPath: CircularIcon
        },
        {
          key: 'highDigital',
          label: 'DII - High digital intensity',
          year: '2025',
          svgPath: CloudIcon
        },
        {
          key: 'digitalIntensity',
          label: 'DII - At least basic digital intensity',
          year: '2024',
          svgPath: MonitorIcon
        },
        {
          key: 'gdpVolume',
          label: 'GDP per capita',
          year: '2024',
          svgPath: GraphIcon
        },
        {
          key: 'productivity',
          label: 'Real labour productivity per person',
          year: '2024',
          svgPath: BagIcon
        },
        {
          key: 'womenEntrepreneurs',
          label: 'Female self-employment rate',
          year: '2025 Q3',
          svgPath: PlusIcon
        }
      ]
    }
  },

  mounted() {
    fetch(mapUrl)
      .then(res => res.text())
      .then(svgText => {
        // Ha nincs preserveAspectRatio, hozzáadjuk
        if (svgText.includes('<svg') && !svgText.includes('preserveAspectRatio')) {
          svgText = svgText.replace('<svg', '<svg preserveAspectRatio="xMidYMid meet"')
        }

        this.svg = svgText

        this.$nextTick(() => {
          this.applyDataClasses()
        })
      })
      .catch(err => console.error('SVG hiba:', err))
  },

  methods: {
    normalizeCountryId(rawId) {
      if (!rawId) return ''

      const value = rawId
        .toLowerCase()
        .trim()
        .replace(/_/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-\d+$/, '')

      const aliases = {
        at: 'austria',
        austria: 'austria',

        ba: 'bosnia',
        bosnia: 'bosnia',
        'bosnia-and-herzegovina': 'bosnia',

        bg: 'bulgaria',
        bulgaria: 'bulgaria',

        hr: 'croatia',
        croatia: 'croatia',

        cz: 'czechia',
        czechia: 'czechia',
        czech: 'czechia',
        'czech-republic': 'czechia',
        'chech-republic': 'czechia',

        de: 'germany',
        germany: 'germany',

        hu: 'hungary',
        hungary: 'hungary',

        md: 'moldova',
        moldova: 'moldova',

        ro: 'romania',
        romania: 'romania',

        rs: 'serbia',
        serbia: 'serbia',

        sk: 'slovakia',
        slovakia: 'slovakia',
        'slovak-republic': 'slovakia',

        si: 'slovenia',
        slovenia: 'slovenia'
      }

      return aliases[value] || ''
    },
    emitCountry() {
      this.$emit('select-country', this.selectedCountry)
    },

    applyDataClasses() {
      const elements = this.$el.querySelectorAll('.map-layer svg path, .map-layer svg g')

      elements.forEach(el => {
        const rawId = el.id || el.getAttribute('data-name')
        if (!rawId) return

        const cleanId = this.normalizeCountryId(rawId)

        if (this.countryData[cleanId]) {
          el.classList.add('has-data')
        }
      })
    },

    getFlagUrl(countryId) {
      if (!countryId) return ''
      const code = countryId.toUpperCase()
      return require(`../../assets/img/we-circular/Contacts_Zászlók/${code}.svg`)
    },

    handleClick(e) {
      const clickedElement = e.target.closest('path, g')
      if (!clickedElement) return

      const rawId = clickedElement.id || clickedElement.getAttribute('data-name')
      const cleanId = this.normalizeCountryId(rawId)

      if (!cleanId || !this.countryData[cleanId]) return

      this.selectedCountry = this.countryData[cleanId]

      const allActive = this.$el.querySelectorAll('.map-layer svg .is-active')
      allActive.forEach(el => {
        el.classList.remove('is-active')
        el.style.fill = ''
        el.style.filter = ''
      })

      const allElements = this.$el.querySelectorAll('.map-layer svg path, .map-layer svg g')
      allElements.forEach(el => {
        const elRawId = el.id || el.getAttribute('data-name')
        const elCleanId = this.normalizeCountryId(elRawId)

        if (elCleanId === cleanId) {
          el.classList.add('is-active')
          el.style.fill = '#1A4691'
          el.style.filter = 'brightness(1.15)'
        }
      })
    },

    closePopup() {
      this.selectedCountry = null

      const activeElements = this.$el.querySelectorAll('.map-layer svg .is-active')
      activeElements.forEach(el => {
        el.classList.remove('is-active')
        el.style.fill = ''
        el.style.filter = ''
      })
    },

    calculateWidth(value, max) {
      if (!value || value === 'N.A') return '0%'
      const num = parseFloat(value)
      return Math.min((num / max) * 100, 100) + '%'
    },

    goToCountry(countryKey) {
      this.$router.push({
        name: 'CountryDetail',
        params: { country: countryKey }
      })
    }
  }
}
</script>

<style scoped>
.country-map-container {
  width: 100%;
}

.country-name {
  padding-right: 10px;
}

.country-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.flag-icon {
  width: 28px;
  height: 20px;
  object-fit: cover;
  border-radius: 2px;
}

.map-layer {
  width: 100%;
  overflow: hidden;
}

.map-layer ::v-deep svg {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
}

.map-layer ::v-deep svg path,
.map-layer ::v-deep svg g {
  pointer-events: none;
  transition: filter 0.3s ease, fill 0.3s ease;
  transform: none !important;
  transform-box: fill-box;
  transform-origin: center;
}

.map-layer ::v-deep .has-data {
  pointer-events: all !important;
  cursor: pointer;
}

.map-layer ::v-deep .has-data:hover {
  filter: brightness(1.08);
}

.map-layer ::v-deep .is-active {
  fill: #003399 !important; /* javítva */
  filter: brightness(1.15);
}

.stats-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  background: rgba(0, 0, 0, 0.25);
}

.stats-card {
  background: #ffffff;
  width: min(900px, 95vw);
  height: auto;
  max-height: 90vh;
  overflow: auto;
  border-radius: 0 0 20px 20px;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.stats-card__header-bg {
  background: url('../../assets/img/we-circular/9-16@4x.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  padding: 15px 40px;
  color: #ffffff;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-card__arrow-link {
  position: absolute;
  top: 10px;
  right: 14px;
  color: #ffffff;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 20;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 40px;
  background: #ffffff;
}

.stat-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.25s ease;
  padding: 10px;
}

.stat-box:hover {
  transform: translateY(-4px);
}

.icon-wrapper {
  width: 56px;
  height: 56px;
  margin: 0 auto 18px;
  border-radius: 10px;
  background: #18baa8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon {
  width: 28px;
  height: 28px;
  display: block;
  object-fit: contain;
}

.stat-label {
  font-size: 13px;
  line-height: 1.2;
  font-weight: 600;
  color: #004444; /* javítva */
  min-height: 32px;
  display: flex;
  align-items: center;
}

.stat-value {
  font-size: 42px;
  font-weight: 700;
  color: #003399; /* javítva */
  margin: 0;
}

.stat-meta {
  font-size: 12px;
  color: #004444; /* javítva */
}

.blue {
  background: #003399; /* javítva */
}

.green {
  background: #18baa8; /* javítva */
}

.orange {
  background: #0e6eb6; /* javítva (design kompatibilisre húzva) */
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 24px;
  }

  .stats-card__header-bg {
    padding: 15px 24px;
  }

  .stat-value {
    font-size: 34px;
  }
}

@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .country-name {
    font-size: 18px;
  }

  .stat-value {
    font-size: 30px;
  }
}
</style>
